/* eslint-disable no-magic-numbers */
// Export const API_URL = "https://localhost:8181/imsWebApi/call";  // for local testing

import { RingGeometry } from 'three';
import { ETextureOrigin, IAssetTexture } from '../@types/ContextProviderRelated';
import { EDebugDetails } from '../@types/Utility';

// PRODUCTION
// ! CAREFUL
export const PLATFORM_URL = 'https://3d-showroom.com/';
export const FILES_API_URL = 'https://data.immersight.com/imsFilesApi';
export const IFC_URL = 'https://bim.immersight.com/floor_plan';

// ALPHA
/*export const PLATFORM_URL = 'https://alpha.3d-showroom.com/';
export const FILES_API_URL = 'https://data-alpha.immersight.com/imsFilesApi';
export const IFC_URL = 'https://alphabim.immersight.com/floor_plan'; */

// BETA
/*export const PLATFORM_URL = 'https://beta.3d-showroom.com/';
export const FILES_API_URL = 'https://data-beta.immersight.com/imsFilesApi';
export const IFC_URL = 'https://betabim.immersight.com/floor_plan'; */

export const KEYCLOAK_ENVIRONMENTS = {
  alpha: {
    url: 'https://iam-alpha.immersight.com/',
    realm: 'immersight',
    clientId: 'ims-floorplanner',
  },
  beta: {
    url: 'https://iam-beta.immersight.com/',
    realm: 'immersight',
    clientId: 'ims-floorplanner',
  },
  production: {
    url: 'https://iam.immersight.com/',
    realm: 'immersight',
    clientId: 'ims-floorplanner',
  },
} as const;

export type KeycloakEnvironment = keyof typeof KEYCLOAK_ENVIRONMENTS;
export const ACTIVE_KEYCLOAK_ENVIRONMENT: KeycloakEnvironment = 'production';
export const KEYCLOAK_CONFIG = KEYCLOAK_ENVIRONMENTS[ACTIVE_KEYCLOAK_ENVIRONMENT];

export const API_URL = PLATFORM_URL + 'imsWebApi/call/';
export const SHOP_LINK = PLATFORM_URL + 'shop/modules?planner=1';
export const ATTACHMENT_URL = PLATFORM_URL + 'imsWebApi/call/attachment';
export const REGISTER_LINK = PLATFORM_URL + 'register';
export const RESET_PW_LINK = PLATFORM_URL + 'account/reset';

export const APP_VERSION = '3.3';
export const JSON_VERSION = '3.0';

/* Size and distance related constants for the planner. */
/**
 * Multiplication factor to be used when converting from centimeters to inches.
 */
export const CM_TO_INCH_MULTIPLIER = 1 / 2.54;
/**
 * Default thickness of a wall in internal units. Equals 10cm.
 */
export const DEFAULT_WALL_DEPTH = 2;
/**
 * Minimum width/length for a door in cm. Default: 30cm.
 */
export const MIN_DOOR_LENGTH = 30;
/**
 * Minimum width for a door in inches.
 */
export const MIN_DOOR_LENGTH_IMPERIAL = Math.round(MIN_DOOR_LENGTH * CM_TO_INCH_MULTIPLIER);
/**
 * Maximum width for a door in cm. Default: 500cm = 5m.
 */
export const MAX_DOOR_LENGTH = 500;
/**
 * Maximum width for a door in inches.
 */
export const MAX_DOOR_LENGTH_IMPERIAL = Math.round(MAX_DOOR_LENGTH * CM_TO_INCH_MULTIPLIER);
/**
 * Minimum thickness of a wall in cm. Default: 5cm.
 */
export const MIN_WALL_DEPTH = 5;
/**
 * Minimum thickness of a wall in inches.
 */
export const MIN_WALL_DEPTH_IMPERIAL = Math.round(MIN_WALL_DEPTH * CM_TO_INCH_MULTIPLIER);
/**
 * Maximum thickness of a wall in cm. Default: 100cm = 1m.
 */
export const MAX_WALL_DEPTH = 100;
/**
 * Maximum thickness of a wall in inches.
 */
export const MAX_WALL_DEPTH_IMPERIAL = Math.round(MAX_WALL_DEPTH * CM_TO_INCH_MULTIPLIER);

/**
 * Minimum length of a wall in cm. Default: 5cm.
 */
export const MIN_WALL_LENGTH = 5;

/**
 * Minimum lenght of a wall in cm for the room shape creator modal. Default: 30cm.
 */
export const MIN_WALL_LENGTH_ROOM_SHAPE_CREATOR = 50;

/**
 * Minimum length of a wall in inches.
 */
export const MIN_WALL_LENGTH_IMPERIAL = Math.round(MIN_WALL_LENGTH * CM_TO_INCH_MULTIPLIER);

/**
 * Minimum length of a wall in inches for the room shape creator.
 */
export const MIN_WALL_LENGTH_ROOM_SHAPE_CREATOR_IMPERIAL = Math.round(MIN_WALL_LENGTH_ROOM_SHAPE_CREATOR * CM_TO_INCH_MULTIPLIER);

/**
 * Maximum length of a wall in cm. Default: 5000cm = 50m.
 */
export const MAX_WALL_LENGTH = 5000;

/**
 * Maximum lenght of a wall for the room shape creator in cm. Default: 3000cm = 30m.
 */
export const MAX_WALL_LENGTH_ROOM_SHAPE_CREATOR = 3000;

/**
 * Maximum length of a wall in inches.
 */
export const MAX_WALL_LENGTH_IMPERIAL = Math.round(MAX_WALL_LENGTH * CM_TO_INCH_MULTIPLIER);

/**
 * Maximum length of a wall in inches.
 */
export const MAX_WALL_LENGTH_ROOM_SHAPE_CREATOR_IMPERIAL = Math.round(MAX_WALL_LENGTH_ROOM_SHAPE_CREATOR * CM_TO_INCH_MULTIPLIER);

/**
 * Minimum width/length for a window in cm. Default: 10cm.
 */
export const MIN_WINDOW_LENGTH = 10;
/**
 * Minimum width/length for a window in inches.
 */
export const MIN_WINDOW_LENGTH_IMPERIAL = Math.round(MIN_WINDOW_LENGTH * CM_TO_INCH_MULTIPLIER);
/**
 * Maximum width/length for a window in cm. Default: 1000cm = 10m.
 */
export const MAX_WINDOW_LENGTH = 1000;
/**
 * Maximum width for a window in inches.
 */
export const MAX_WINDOW_LENGTH_IMPERIAL = Math.round(MAX_WINDOW_LENGTH * CM_TO_INCH_MULTIPLIER);

/**
 * Minimum length for a radiator in cm. Default: 10cm.
 */
export const MIN_RADIATOR_LENGTH = 10;
/**
 * Minimum length for a radiator in inches.
 */
export const MIN_RADIATOR_LENGTH_IMPERIAL = Math.round(MIN_RADIATOR_LENGTH * CM_TO_INCH_MULTIPLIER);
/**
 * Maximum length for a radiator in cm. Default: 1000cm = 10m.
 */
export const MAX_RADIATOR_LENGTH = 1000;
/**
 * Maximum length/width for a radiator in inches.
 */
export const MAX_RADIATOR_LENGTH_IMPERIAL = Math.round(MAX_RADIATOR_LENGTH * CM_TO_INCH_MULTIPLIER);

export const MIN_RADIATOR_DEPTH = 1;
export const MIN_RADIATOR_DEPTH_IMPERIAL = Math.round(MIN_RADIATOR_DEPTH * CM_TO_INCH_MULTIPLIER);
export const MAX_RADIATOR_DEPTH = 100;
export const MAX_RADIATOR_DEPTH_IMPERIAL = Math.round(MAX_RADIATOR_DEPTH * CM_TO_INCH_MULTIPLIER);

/**
  * 
/**
 * Default value for the width of the rendered lines of the length indicators.
 */
export const DEFAULT_INDICATOR_LINE_WIDTH = 2;

/**
 * Default distance between the element distance indicators and the wall.
 */
export const DEFAULT_INDICATOR_WALL_DISTANCE = 0.75;

/**
 * Length of a side of the square Canvas.
 */
export const SINGLE_CANVAS_SIDE_SIZE = 1800;
/**
 * Conversion factor to divide internal units and get meter as a result.
 */
export const UNIT_TO_METER_DIVISOR = 10;
/**
 * Conversion factor to multiply cm values to internal units. 1 cm = 0.1 units in internal representation.
 */
export const CM_TO_INTERNAL_FACTOR = 0.1;
/**
 * Conversion factor to multiply mm values to internal units. 1 mm = 0.01 units in internal representation.
 */
export const MM_TO_INTERNAL_FACTOR = 0.01;
/**
 * Conversion factor to multiply inch values to internal units. 1 inch = 0.254 units in internal representation.
 */
export const IN_TO_INTERNAL_FACTOR = 0.254;
/**
 * Conversion factor to multiply cm and get internal units as a result.
 */
export const INTERNAL_TO_CM_FACTOR = 1 / CM_TO_INTERNAL_FACTOR;
/**
 * /**
 * Conversion factor to multiply cm and get internal units as a result.
 */
export const INTERNAL_TO_MM_FACTOR = 1 / MM_TO_INTERNAL_FACTOR;
/**
 * Conversion factor to multiply inches and get internal units as a result.
 */
export const INTERNAL_TO_IN_FACTOR = 1 / IN_TO_INTERNAL_FACTOR;

/**
 * Default zoom value that is used for the camera in the Three.Scene.
 */
export const DEFAULT_ZOOM_VALUE = 15;
/**
 * Minimum zoom value that the camera is allowed to inherit.
 */
export const MIN_ZOOM_VALUE = 4;
/**
 * Maximum zoom value that the camera is allowed to inherit.
 */
export const MAX_ZOOM_VALUE = 60;
/**
 * The Scale Widget's measurement at default zoom level. Adjust this value to change the range of the scale.
 */
export const SCALE_DEFAULT_MEASUREMENT = 100;

// Convert the scale widgets line to pixel.
export const SCALE_LINE_TO_PIXEL = 15;

// Convert centimeters to meters.
export const CM_TO_M = 0.01;

/**
 * Conversion factor for the scale widget to convert inches into meters.
 */
export const IN_TO_M = 0.0254;
/**
 * The Scale Widget's measurement will get rounded to the nearest interval (= multiple of this number). For 5 => ... 95, 100, 105, ...
 */
export const SCALE_INTERVAL = 5;
/**
 * Internal conversion factor to multiply the zoom line values to calculate the lengths correctly.
 */
export const SCALE_LINE_TO_TEN = 10;
/**
 * Conversion factor to get cm from meter values.
 */
export const CM_TO_M_DIVISOR = 100;

/**
 * Offset towards the outer wall for the wall length indicator.
 * The long line of the indicator will run parallel to the wall with this distance.
 */
export const DEFAULT_WALL_OUTER_OFFSET = 2;
/**
 * Offset towards the inner wall for the wall length indicator.
 * The long line of the indicator will run parallel to the wall with this distance.
 */
export const DEFAULT_WALL_INNER_OFFSET = 1.0;
/**
 * Adjusted offset for the inner wall length indicator to accomodate for the displayed element distance indicators.
 */
export const INNER_WALL_OFFSET_ADJUSTED = 1.8;
/**
 * Offset for the wall text for the wall length indicator.
 */
export const WALL_TEXT_OFFSET = 0.6;
/**
 * Offset value to display the perimeter value.
 */
export const PERIMETER_OFFSET = 1.5;

/**
 * Maximum number of upper floors that a building can have.
 */
export const MAX_UPPER_FLOOR_LEVELS = 10;

/**
 * Offset from the z-axis inside of the Three.Scene.
 * Is used to render certain objects always in the front.
 */
export const Z_AXIS_OFFSET = 0.1;
/**
 * Conversion factor to divide the theta angle by.
 */
export const THETA_ANGLE_DIVISOR = 50;

/**
 * X value for a sphereGeometry from Three.js. Is used for the draggable point.
 */
export const SPHERE_GEOMETRY_X = 0.6;
/**
 * Y and Z value for a sphereGeometry from Three.js. Is used for the draggable point.
 */
export const SPHERE_GEOMETRY_YZ = 32;

/**
 * Horizontal distance between snap lines.
 */
export const SNAP_LINE_DISTANCE_HORIZONTAL = 500;
/**
 * Vertical distance between snap lines.
 */
export const SNAP_LINE_DISTANCE_VERTICAL = 100;

/**
 * Default length/width of a window.
 */
export const DEFAULT_WINDOW_LENGTH = 6;
/**
 * Default length/width of a door.
 */
export const DEFAULT_DOOR_LENGTH = 8;
/**
 * Default length/width of a passage.
 */
export const DEFAULT_PASSAGE_LENGTH = DEFAULT_DOOR_LENGTH;
/**
 * Minimum width/length for a passage in cm.
 */
export const MIN_PASSAGE_LENGTH = MIN_DOOR_LENGTH;
/**
 * Minimum width for a passage in inches.
 */
export const MIN_PASSAGE_LENGTH_IMPERIAL = MIN_DOOR_LENGTH_IMPERIAL;

/**
 * Default length/width of a radiator.
 */
export const DEFAULT_RADIATOR_LENGTH = 10;
/**
 * Default depth/thickness of a radiator.
 */
export const DEFAULT_RADIATOR_DEPTH = 1;

// 0 means no niche/inset
export const DEFAULT_RADIATOR_NICHE_DEPTH = 0;

/**
 * Conversion factor to calculate the center of a polygon shape.
 */
export const POLYGON_CENTROID_MULTIPLIER = 6;
/**
 * Icon size value for a small icon.
 */
export const ICON_SIZE_SMALL = 50;
/**
 * Icon size value for a medium icon.
 */
export const ICON_SIZE_MEDIUM = 75;
/**
 * Icon size value for a large icon.
 */
export const ICON_SIZE_LARGE = 200;

/**
 * Number identifier of the first sample room.
 */
export const SAMPLE_ROOM_1_ID = 1;
/**
 * Number identifier of the second sample room.
 */
export const SAMPLE_ROOM_2_ID = 2;
/**
 * Number identifier of the third sample room.
 */
export const SAMPLE_ROOM_3_ID = 3;
/**
 * Number identifier of the fourth sample room.
 */
export const SAMPLE_ROOM_4_ID = 4;
/**
 * Number amount of rooms inside of the first sample room.
 */
export const WALL_AMOUNT_ROOM_1 = 4;
/**
 * Number amount of rooms inside of the second sample room.
 */
export const WALL_AMOUNT_ROOM_2 = 6;
/**
 * Number amount of rooms inside of the third sample room.
 */
export const WALL_AMOUNT_ROOM_3 = 8;
/**
 * Number amount of rooms inside of the fourth sample room.
 */
export const WALL_AMOUNT_ROOM_4 = 5;

// Explicitly typing the room IDs
export type SampleRoomId = typeof SAMPLE_ROOM_1_ID | typeof SAMPLE_ROOM_2_ID | typeof SAMPLE_ROOM_3_ID | typeof SAMPLE_ROOM_4_ID;

/**
 * Default wall lengths for different room types in the SelectRoomSizeModal window, in cm.
 * Each key represents a room type, and the corresponding value is an object with default wall lengths.
 */
export const DEFAULT_WALL_LENGTHS: {
  [key: string]: { [key: string]: number };
} = {
  [SAMPLE_ROOM_1_ID]: { A: 300, B: 300, C: 300, D: 300 },
  [SAMPLE_ROOM_2_ID]: { A: 300, B: 300, C: 100, D: 100 },
  [SAMPLE_ROOM_3_ID]: { A: 300, B: 200, C: 100, D: 100 },
  [SAMPLE_ROOM_4_ID]: { A: 300, B: 200, D: 200, E: 300 },
};
/**
 * Default wall length in inches (used when the unit system is imperial).
 */
export const DEFAULT_WALL_LENGTH_IMPERIAL = {
  [SAMPLE_ROOM_1_ID]: {
    A: Math.round(DEFAULT_WALL_LENGTHS[SAMPLE_ROOM_1_ID].A * CM_TO_INCH_MULTIPLIER),
    B: Math.round(DEFAULT_WALL_LENGTHS[SAMPLE_ROOM_1_ID].B * CM_TO_INCH_MULTIPLIER),
    C: Math.round(DEFAULT_WALL_LENGTHS[SAMPLE_ROOM_1_ID].C * CM_TO_INCH_MULTIPLIER),
    D: Math.round(DEFAULT_WALL_LENGTHS[SAMPLE_ROOM_1_ID].D * CM_TO_INCH_MULTIPLIER),
  },
  [SAMPLE_ROOM_2_ID]: {
    A: Math.round(DEFAULT_WALL_LENGTHS[SAMPLE_ROOM_2_ID].A * CM_TO_INCH_MULTIPLIER),
    B: Math.round(DEFAULT_WALL_LENGTHS[SAMPLE_ROOM_2_ID].B * CM_TO_INCH_MULTIPLIER),
    C: Math.round(DEFAULT_WALL_LENGTHS[SAMPLE_ROOM_2_ID].C * CM_TO_INCH_MULTIPLIER),
    D: Math.round(DEFAULT_WALL_LENGTHS[SAMPLE_ROOM_2_ID].D * CM_TO_INCH_MULTIPLIER),
  },
  [SAMPLE_ROOM_3_ID]: {
    A: Math.round(DEFAULT_WALL_LENGTHS[SAMPLE_ROOM_3_ID].A * CM_TO_INCH_MULTIPLIER),
    B: Math.round(DEFAULT_WALL_LENGTHS[SAMPLE_ROOM_3_ID].B * CM_TO_INCH_MULTIPLIER),
    C: Math.round(DEFAULT_WALL_LENGTHS[SAMPLE_ROOM_3_ID].C * CM_TO_INCH_MULTIPLIER),
    D: Math.round(DEFAULT_WALL_LENGTHS[SAMPLE_ROOM_3_ID].D * CM_TO_INCH_MULTIPLIER),
  },
  [SAMPLE_ROOM_4_ID]: {
    A: Math.round(DEFAULT_WALL_LENGTHS[SAMPLE_ROOM_4_ID].A * CM_TO_INCH_MULTIPLIER),
    B: Math.round(DEFAULT_WALL_LENGTHS[SAMPLE_ROOM_4_ID].B * CM_TO_INCH_MULTIPLIER),
    D: Math.round(DEFAULT_WALL_LENGTHS[SAMPLE_ROOM_4_ID].D * CM_TO_INCH_MULTIPLIER),
    E: Math.round(DEFAULT_WALL_LENGTHS[SAMPLE_ROOM_4_ID].E * CM_TO_INCH_MULTIPLIER),
  },
};
/**
 * Default names for the customizable walls in the sample rooms.
 */
export const WALL_NAMES = ['A', 'B', 'C', 'D', 'E', 'F', 'G', 'H'];

/**
 * Maximum size of a tile in cm. 500cm = 5m.
 */
export const MAX_TILE_SIZE = 500;
/**
 * Maximum size of a tile in inches.
 */
export const MAX_TILE_SIZE_IMPERIAL = Math.round(MAX_TILE_SIZE * CM_TO_INCH_MULTIPLIER);
/**
 * Minimum width of a joint between two tiles in cm. 0.1cm = 1mm.
 */
export const MIN_TILE_JOINT_WIDTH = 0.1;
/**
 * Minimum width of a joint between two tiles in inches.
 */
export const MIN_TILE_JOINT_WIDTH_IMPERIAL = Math.round(MIN_TILE_JOINT_WIDTH * CM_TO_INCH_MULTIPLIER);

/**
 * Thickness of the visual representation of the snap lines.
 */
export const SNAP_LINE_THICKNESS = 0.2;
/**
 * Size value for a Tube object needed for calculations.
 */
export const TUBE_SIZE = 64;
/**
 * Size value for the radial segments of a Tube object needed for calculations.
 */
export const TUBE_RADIAL_SEGMENTS = 8;

/**
 * Height padding value for the drawer box.
 */
export const DRAWER_BOX_HEIGHT_PADDING = 7;

/**
 * Minimally allowed angle where a line can snap to another one.
 */
export const SNAP_ANGLE_THRESHOLD = 2.4;
/**
 * Minimally allowed threshold distance before a snap on a snap line can occur. Adjust value as needed.
 */
export const SNAP_THRESHOLD = 1;
/**
 * Numerical tolerance to calculate a room completion.
 */
export const ROOM_COMPLETION_TOLERANCE = 3;

/* Debugging related. */

/**
 * Global debugging flag that can get used as single source of truth for all other debugging related stuff, that can get toggled.
 */
export const GLOBAL_DEBUG_ENABLED = false;
/**
 * Specific flag to specify if the output of the scene element debugger should get printed to the console.
 */
export const SCENE_DEBUGGER_OUTPUT = false;
/**
 * Describes the amount of details that should get printed to the console when the scene element debugger is active.
 */
export const SCENE_DEBUGGER_DETAIL_AMOUNT = EDebugDetails.detailed;
/**
 * Boolean flag to indicate if the SceneElementDebugger will print information related to the matrices of the individual elements in the scene.
 * Amount of details will depend on the SCENE_DEBUGGER_DETAIL_AMOUNT flag above.
 */
export const SCENE_DEBUGGER_SHOW_MATRIX = true;
/**
 * Flag to indicate if the current position of the cursor on our canvas should get displayed next to it.
 * Is implemented in the CoordinatePointer component and handled in the SceneElementDebugger as well.
 */
export const SHOW_CANVAS_COORDINATES = false;
/**
 * Padding that gets applied to the background box of the coordinate panel next to the cursor.
 */
export const COORDINATE_BOX_PADDING = 0.2;
/**
 * Font size for the displayed position coordinates.
 */
export const COORDINATE_FONT_SIZE = 0.8;
/**
 * Internal utility value to estimate the width of the background box behind the coordinates.
 */
export const COORDINATE_WIDTH_MULTIPLIER = 0.3;
/**
 * Internal utility value to estimate the height of the background box behind the coordinates.
 */
export const COORDINATE_HEIGHT_MULTIPLIER = 3;
/**
 * Internal utility value to estimate a consistent and constant width for the background box of the coordinate panel.
 */
export const COORDINATE_PANEL_FIXED_WIDTH = 18;

/* Other replacements for magic numbers. */

/**
 * Amount of degrees in a full circle. 360.
 */
export const FULL_CIRCLE_DEGREES = 360;
/**
 * Amount of degrees in a half circle. 180.
 */
export const HALF_CIRCLE_DEGREES = 180;
/**
 * Amount of degrees in a quarter circle. 90.
 */
export const QUARTER_CIRCLE_DEGREES = 90;
/**
 * Default font size in px. Needed for calculating rem related sizes.
 */
export const DEFAULT_FONT_SIZE_IN_PX = 16;
/**
 * Default font size for the length indicators.
 */
export const DEFAULT_INDICATOR_FONT_SIZE = 0.8;
/**
 * Default font size for the length indicators that need a marginally smaller font.
 * Currently used for windows and doors.
 */
export const DEFAULT_INDICATOR_FONT_SIZE_XS = (DEFAULT_INDICATOR_FONT_SIZE / 9) * 8;
/**
 * Default value for the line height that a RobotoText object in our app will have.
 */
export const DEFAULT_TEXT_LINE_HEIGHT = 1;
/**
 * Minimum value for a font that gets its size changed according to the zoom factor of the camera on the scene.
 */
export const MIN_ZOOM_FONT_SIZE = 0.8;
/**
 * Default value for the font size if no optional parameter was provided.
 */
export const DEFAULT_ZOOM_FONT_SIZE = 1;
/**
 * Maximum value for a font that gets its size changed according to the zoom factor of the camera on the scene.
 */
export const MAX_ZOOM_FONT_SIZE = 2.2;
/**
 * Conversion factor that is used for calculating the appropriate font size according to the zoom level changes.
 */
export const FONT_SIZE_ZOOM_FACTOR = 14;
/**
 * Path to the roboto font file that we provide in our public folder to get bundled with the app.
 */
export const ROBOTO_FONT_PATH = '/fonts/Roboto-Regular.ttf';
/**
 * Internal conversion factor. Self explanatory.
 */
export const HALF = 0.5;
/**
 * Partial sub value for the black grid color.
 */
export const GRID_COLOR_BLACK = 0.3;
/**
 * Partial sub value for the grey grid color.
 */
export const GRID_COLOR_GREY = 0.8;
/**
 * Value needed to calculate the grid structure.
 */
export const GRID_MODULO_DIVISOR = 40;
/**
 * Numerical key code of the enter key.
 */
export const KEY_CODE_ENTER = 13;
/**
 * Numerical percentage value to represent 100%.
 */
export const PERCENTAGE = 100;
/**
 * Internal enlargement value to display tiles correctly.
 */
export const ENLARGEMENT_FACTOR = 10;

/* Times. */

/**
 * Length of the intro animation in milliseconds.
 */
export const INTRO_LENGTH = 3000;
/**
 * Length of the threshold before a long press event gets triggered when clicking on a wall in milliseconds.
 */
export const LONG_PRESS_TIME_WALLS = 100;
/**
 * Default length of the threshold before a long press event gets triggered when clicking on something in milliseconds.
 */
export const LONG_PRESS_TIME_DEFAULT = 300;
/**
 * Length of the threshold before a long press event gets triggered when clicking on a button in the toolbar in milliseconds.
 */
export const LONG_PRESS_TIME_TOOLBAR = 600;
/**
 * Length of the timeout that is needed to start a React re-render cycle in milliseconds.
 */
export const TIMEOUT_LENGTH_FOR_RERENDER = 1000;

/* Image paths, units and other miscellaneous. */

/**
 * File path of the image used for the compass widget.
 */
export const COMPASS_IMAGE_PATH = './img/compass_rose_black_512.png';
/**
 * Unscaled size of one grid small box (10cm) with zoom = 15, in rem (css rem unit).
 */
export const BASE_UNIT_REM = 10.35 / 11;
/**
 * Default snap distance function parameter default value.
 */
export const DEFAULT_SNAP_DISTANCE_PARAM_VALUE = 2.4;
/**
 * Opacity value to be used for invalid tiles.
 */
export const INVALID_TILE_OPACITY = 0.8;
/**
 * Distance of the camera to the canvas.
 */
export const CAMERA_DISTANCE = 20;
/**
 * X-, y- and z-Position value of the Light object on the Scene.
 */
export const LIGHT_POSITION = 10;

/* Object default color. */
/**
 * Default color of the floor.
 */
export const DEFAULT_FLOOR_COLOR = '#DDDDDD';
/**
 * Default color for the joints between tiles.
 */
export const DEFAULT_JOINT_COLOR = '#FFFFFF';
/**
 * Default color for the lines of the length indicators.
 */
export const DEFAULT_INDICATOR_COLOR = '#8e8e8e';

/* Export pdf related constants. */

/**
 * Width of a DIN A4 page in mm.
 */
export const DINA4_PAGE_WIDTH = 210;
/**
 * Height of a DIN A4 page in mm.
 */
export const DINA4_PAGE_HEIGHT = 297;
/**
 * Combined dimension object for a din a4 page.
 */
export const DIN_A4_DIMENSIONS = {
  width: DINA4_PAGE_WIDTH,
  height: DINA4_PAGE_HEIGHT,
};
/**
 * Conversion factor for font size to mm.
 */
export const FONT_TO_MM = 0.35278;
/**
 * Master padding between the edge of the paper and all pdf content in mm.
 */
export const MASTER_PAGE_PADDING = 12;
/**
 * Font size of the title in the exported pdf.
 */
export const TITLE_FONT_SIZE = 20;
/**
 * Spacing of the title at the bottom.
 */
export const TITLE_SPACING_BOTTOM = 4;
/**
 * Percentage value of the actual size that should get used.
 * if the image height is larger than x% use the full page (instead of trying to start text)
 */
export const USE_FULL_SIZE_PERCENTAGE = 0.9;
/**
 * Default image upscale factor to increase the resolution.
 * default factor for the image size in the pdf (overwritten by params) - heavily influences pdf filesize
 */
export const DEFAULT_IMAGE_UPRES = 8;
/**
 * Image spacing at the bottom.
 * the spacing below the image
 */
export const IMAGE_SPACING_BOTTOM = 8;
/**
 * Padding value on the x-axis for the divider between images. this is subtracted from the divider-line's width
 */
export const IMAGE_DIVIDER_PADDING_X = 20;
/**
 * Flag to indicate whether or not to draw a black border around images.
 */
export const DRAW_IMAGE_BORDER = false;
/**
 * Font size for regular text in the exported pdf.
 */
export const TEXT_FONT_SIZE = 14;
/**
 * Padding value on the x-axis for the text in the export.
 * indent (left and right) of the text
 */
export const TEXT_PADDING_X = 16;
/**
 * Padding value on the Y-axis for the text block in the export.
 * spacing between each text block
 */
export const TEXT_BLOCK_PADDING_Y = 4;
/**
 * Font size to be used for the page numbers in the exported pdfs.
 */
export const PAGE_NUMBER_FONT_SIZE = 10;
/**
 * Spacing above the footer.
 */
export const FOOTER_SPACING_TOP = 8;
/**
 * Padding left and right for the header
 */
export const HEADER_PADDING_X = 10;
/**
 * Spacing above the header
 */
export const HEADER_SPACING_TOP = 8;
/**
 * Spacing below the header
 */
export const HEADER_SPACING_BOTTOM = 8;
/**
 * Spacing below the headers text
 */
export const HEADER_FONT_SPACING_BOTTOM = 2;
/**
 * Header font size
 */
export const HEADER_FONT_SIZE = 12;
/**
 * Size of the compass rose (same height and width)
 */
export const COMPASS_SIZE = 24;
/**
 * Spacing between compass and top-right corner of scene image
 */
export const COMPASS_PADDING = { x: 4, y: 4 };
/**
 * Spacing between the compass image and the background rect
 */
export const COMPASS_INNER_PADDING = { x: 1, y: 2 };
/**
 * Spacing between the compass image and the text below
 */
export const COMPASS_SPACING_TO_TEXT = 2;
/**
 * Font size of the text below the compass image
 */
export const COMPASS_FONT_SIZE = 12;
/**
 * Color of the background rect
 */
export const COMPASS_BACKGROUND_COLOR = '#ffffff';
/**
 * Opacity of the background rect
 */
export const COMPASS_BACKGROUND_OPACITY = 0.5;
/**
 * Whether to draw a black border around the background rect
 */
export const COMPASS_DRAW_CONTAINER_BORDER = false;
// The line thickness of horizontal dividers.
export const HORIZONTAL_DIVIDER_LINE_THICKNESS = 0.5;

// How much the scale should be moved upwards into the image.
export const PDF_SCALE_UP_TRANSLATE = 4;
// Spacing of pdf scale to the right of the page (in MM).
export const PDF_SCALE_PADDING_RIGHT = 10;
// Spacing of pdf scale between line and label (in MM).
export const PDF_SCALE_LABEL_GAP = 2;
// Thickness of the pdf scale's line (in MM).
export const PDF_SCALE_LINE_THICKNESS = 1;
// Font size of the pdf scale's label.
export const PDF_SCALE_LABEL_FONTSIZE = 14;

/* Export Image Config values. */

/**
 * Default auto reference slice for setting the maximum canvas size.
 */
export const DEFAULT_AUTO_REFERENCE_SLICE = 1024;
/**
 * Default value for multiplying while calculating the max canvas size.
 */
export const DEFAULT_CANVAS_MAX_SIZE_MULTIPLIER = 6;
/**
 * Default value for the border margin around the bounding box borders of walls when exported.
 * Represents the lower bound that this value can take.
 */
export const DEFAULT_BORDER_MARGIN_EXPORT_WALLS = 0.01;
/**
 * Default background color of the exported image.
 */
export const DEFAULT_BACKGROUND_COLOR = '#ffffff';
/**
 * Default flag to indicate if the exported image should be transparent.
 */
export const DEFAULT_TRANSPARENT = false;
/**
 * Default file format for image exports.
 */
export const DEFAULT_FORMAT = 'png';
/**
 * Default file name of the exported image.
 */
export const DEFAULT_FILENAME = 'unnamed_floorplan';
/**
 * Default value to indicate how many pixels to draw into the export per dm.
 */
export const DEFAULT_PIXEL_PER_DECIMETER = 80;
/**
 * Compass size for image exports.
 * relative to the min image dimension
 */
export const IMG_COMPASS_SIZE = 0.2;
/**
 * Compass padding for image exports.
 * relative to the compass_size
 */
export const IMG_COMPASS_PADDING = 0.1;
/**
 * Compass spacing to the text for image exports.
 * relative to the compass_size
 */
export const IMG_COMPASS_SPACING_TO_TEXT = 0.125;
/**
 * Compass font size for image exports.
 * relative to the compass_size
 */
export const IMG_COMPASS_FONT_SIZE = 0.15;
/**
 * Compass font css suffix for image exports.
 * relative to the compass_size
 */
export const IMG_COMPASS_FONT_CSS_SUFFIX = 'px Roboto';

/**
 * Fixed image reference size for the scale's layout.
 * To ensure that the scale's label and thickness is
 * always approximately the same size, no matter the
 * image size/resolution.
 */
export const IMG_SCALE_REF_SIZE = [1000, 1000];
// (image export) The scale's font size, relative to the IMG_SCALE_REF_SIZE
export const IMG_SCALE_REF_FONTSIZE = 22;
// (image export) The scale's padding to the bottom of the image, relative to the IMG_SCALE_REF_SIZE
export const IMG_SCALE_REF_PADDING_BOTTOM = 8;
// (image export) The scale's padding to the right of the image, relative to the IMG_SCALE_REF_SIZE
export const IMG_SCALE_REF_PADDING_RIGHT = 8;
// (image export) The scale's line thickness, relative to the IMG_SCALE_REF_SIZE
export const IMG_SCALE_REF_LINE_THICKNESS = 5;
// (image export) The color of the scale.
export const IMG_SCALE_COLOR = '#000000';
// (image export) The scale's font.
export const IMG_SCALE_FONT = 'Roboto';

/* Export scale indicator config values. */

/**
 * Internal factor to calculate the line length for the scale indicator.
 * adjust the line range
 */
export const LINE_LENGTH_FACTOR = 0.04;
/**
 * Interval in cm for the scale indicator. Do not set to 0!
 */
export const INTERVAL_CM = 5;
/**
 * Thickness of a single line in the scale indicator export.
 * adjust the thickness of the line (relative),
 */
export const LINE_THICKNESS = 0.15;
/**
 * Line offset on the right for the scale indicator.
 * space between the line and the right edge of the image
 */
export const LINE_OFFSET_RIGHT = 4;
/**
 * Line offset on the bottom for the scale indicator.
 * space between the line (center) and the bottom edge of the image
 */
export const LINE_OFFSET_BOTTOM = 1;
/**
 * Text offset on the right for the scale indicator.
 * space between the text (center) and the right edge of the image
 */
export const TEXT_OFFSET_RIGHT = 2;
/**
 * Text offset on the bottom for the scale indicator.
 * space between the text (center) and the bottom edge of the image
 */
export const TEXT_OFFSET_BOTTOM = 1;
/**
 * Flag to indicate whether or not to show the debugging component for the export functionality.
 * Set to false to disable displaying the buttons.
 */
export const SHOW_EXPORT_TESTING_BUTTONS = GLOBAL_DEBUG_ENABLED;
/**
 * Value to indicate by which amount we need to multiply the retrieved compass height,
 * to account for the case when the compass needs to get drawn to the export.
 */
export const PDF_CURSOR_HEIGHT_OFFSET_MULTIPLIER = 0.6;
/**
 * Value to be used as a substitute for any case where another value might be zero and would result in division by zero.
 */
export const PREVENT_DIVISION_BY_ZERO = 0.01;
/**
 * Amount of milliseconds that have to get waited for the calculation of the scene image.
 */
export const CREATE_SCENE_IMAGE_SLEEP_TIME = 100;
/**
 * Value to calculate the padding of the canvas.
 * Takes the size of the canvas and proportionally adjusts the padding.
 */
export const CANVAS_PADDING_FACTOR = 0.1;
/**
 * Distance number value for the near plane of the camera on the scene.
 */
export const CAMERA_NEAR_DISTANCE = 0.1;
/**
 * Distance number value for the far plane of the camera on the scene.
 */
export const CAMERA_FAR_DISTANCE = 2000;
/**
 * Z-index value of the camera in the 3D space.
 */
export const CAMERA_Z_INDEX = 100;
/**
 * Value to calculate the line thickness in geometric units.
 */
export const LINE_THICKNESS_GEOMETRIC_UNIT_DIVISOR = 15;
/**
 * Value representing the viewport scale factor of the camera.
 */
export const CAMERA_VIEWPORT_SCALE_FACTOR = 0.016;
/**
 * Numerical value to calculate the line scale length.
 */
export const LINE_SCALE_LENGTH_FACTOR = 0.1;
/**
 * Relative Font Size of the measurement text of the scale in exports.
 */
export const EXPORT_SCALE_FONT_SIZE = 1.0;
/**
 * Maximum value of an 8 bit int value like in RGB.
 */
export const RGB_8BIT_MAX_VALUE = 255;
/**
 * Three.RingGeometry object that is needed for the export process.
 */
export const RING_GEOMETRY_CENTER = new RingGeometry(0.2, 0.25, 32, 8);

/**
 * Describes the maximum font size for the in-scene text labels during an export.
 */
export const MAX_EXPORT_SCENE_FONT_SIZE = 4;
/**
 * Describes the minimum font size for the in-scene text labels during an export.
 */
export const MIN_EXPORT_SCENE_FONT_SIZE = 0.35;
/**
 * Describes the upper threshold room perimeter size that has to be exceeded before the minimum font size will get applied to the export.
 */
export const EXPORT_ROOM_PERIMETER_UPPER_THRESHOLD = 50;
/**
 * Describes the lower threshold where the room perimeter size needs to be lower or equal, before the maximum font size will get applied to the export.
 */
export const EXPORT_ROOM_PERIMETER_LOWER_THRESHOLD = 7;
/**
 * Describes the threshold that the aspect ratio of the building's bounding box has to cross, before we need to reconsider the scaling of the font size.
 */
export const EXPORT_BUILDING_MAX_RATIO = 1.5;
/**
 * Describes the maximum amount for shifting text during export.
 * Will get used to calculate the actual shift, where this value is correlating to the largest export font size.
 */
export const MAX_EXPORT_TEXT_SHIFT = 1.5;
/**
 * Describes the minimum amount for shifting text during export.
 * Will get used to calculate the actual shift, where this value is correlating to the smallest export font size.
 */
export const MIN_EXPORT_TEXT_SHIFT = 0.05;

/* Download Modal related configs. */
/**
 * Cooldown of the download button in milliseconds. Set to x <= 0 to disable. Prevents spamming bugs.
 */
export const DOWNLOAD_BUTTON_COOLDOWN_MS = 1000;

/**
 * Specifies the tolerance for checking the proximity of wall points to the floor polygon.
 */
export const POLYGON_BOUNDARY_TOLERANCE = 3;

/**
 * Describes the default amount of columns that will get displayed in the ProductGallery, which is located in the ProductDrawer.
 */
export const PRODUCT_GALLERY_DEFAULT_COLS = 3;

/**
 * Describes the minimum height of a row inside of the image gallery in pixels.
 */
export const PRODUCT_GALLERY_MIN_ROW_HEIGHT = 110;

/**
 * Describes the maximum height of a row inside of the image gallery in pixels.
 */
export const PRODUCT_GALLERY_MAX_ROW_HEIGHT = 300;

/**
 * Describes the default amount of space in pixels between individual items in the image list.
 */
export const PRODUCT_GALLERY_DEFAULT_GAP = 8;

/**
 * Describes the timing function that gets applied to all animations in the product gallery during a transition.
 */
export const PRODUCT_GALLERY_TRANSITION_TIMING_FUNCTION = 'cubic-bezier(.7,.11,.47,.7)';

/**
 * Describes the duration of an animation during an transition inside of the product gallery.
 */
export const PRODUCT_GALLERY_TRANSITION_DURATION = '1.5s';

/**
 * Describes the amount of delay that will get applied to the transform transitions of the ListItemBar in the ProductGallery component.
 * This will ensure that when the mouse quickly passes over them while scrolling down, the animations aren't all getting triggered.
 * First value is delay before a transition starts, second value is the delay that gets applied before the inverse transformation gets executed at the end.
 */
export const PRODUCT_GALLERY_TRANSITION_DELAY = '400ms';

/**
 * Combines the previous values into a singular string that can be applied to all transitions in the product gallery.
 */
export const PRODUCT_GALLERY_TRANSITION = `all ${PRODUCT_GALLERY_TRANSITION_DURATION} ${PRODUCT_GALLERY_TRANSITION_DELAY} ${PRODUCT_GALLERY_TRANSITION_TIMING_FUNCTION}`;

/**
 * Describes the amount of delay in milliseconds that will be waited after each new user keystroke.
 * Only after we have not received another keystroke for this amount of time, we infer that the user has finished typing and we start our search query thereafter.
 */
export const PRODUCT_GALLERY_INPUT_SEARCH_DELAY = 550;

/**
 * Describes the maximum width in pixels that a tooltip for a product can have.
 */
export const PRODUCT_TOOLTIP_MAX_WIDTH = 350;

/**
 * String describing the background opacity of the tooltip popper windows in percent.
 */
export const PRODUCT_TOOLTIP_BACKGROUND_OPACITY = '90%';

/**
 * Describes the amount of time that it takes the product tooltip to appear when transitioning.
 */
export const PRODUCT_TOOLTIP_TRANSITION_TIMEOUT_APPEAR = 350;
/**
 * Describes the amount of time that it takes the product tooltip to enter when transitioning.
 */
export const PRODUCT_TOOLTIP_TRANSITION_TIMEOUT_ENTER = 100;
/**
 * Describes the amount of time that it takes the product tooltip to exit when transitioning.
 */
export const PRODUCT_TOOLTIP_TRANSITION_TIMEOUT_EXIT = 400;

/**
 * The transition timing function for the initial transition on enter.
 */
export const PRODUCT_TOOLTIP_ENTER_TRANSITION_TIMING_FUNCTION = 'cubic-bezier(.68,.1,.93,1.0)';
/**
 * The transition timing function for the resulting transition on exit.
 */
export const PRODUCT_TOOLTIP_EXIT_TRANSITION_TIMING_FUNCTION = 'cubic-bezier(.79,.24,.72,.95)';

/**
 * Describes the default width of the side drawer component in pixels.
 */
export const SIDE_DRAWER_DEFAULT_WIDTH = 300;

/**
 * Describes the default wall thickness that every wall was assigned in the old floorplanner.
 * 15 cm = 1.5 internal units.
 */
export const OLD_WALL_THICKNESS = 1.5;

/**
 * Describes the default width parameter for the ImsIcon component.
 */
export const IMS_ICON_DEFAULT_WIDTH = '24px';
/**
 * Describes the default height parameter for the ImsIcon component.
 */
export const IMS_ICON_DEFAULT_HEIGHT = '24px';
/**
 * Describes the default color fill parameter for the ImsIcon component.
 */
export const IMS_ICON_DEFAULT_FILL = 'currentColor';
/**
 * Describes the default viewbox for the svg inside of a MUI icon.
 * Allows us to set the amount of units that get applied to the inside of the svg while calculating.
 * So even if our svg files have an original viewbox of 0 0 500 500, we can set it to 0 0 24 24, we won't have 250000 individual points on there, but the 576 points as MUI icons.
 * Can be used to create svg icons that scale and behave the same as the others from MUI.
 */
export const MUI_ICON_VIEWBOX = '0 0 24 24';
/**
 * Default font size of the ims icon.
 */
export const IMS_ICON_DEFAULT_FONT_SIZE = '1.75rem';

/**
 * Default delay in milliseconds that will get applied to a value in the useDebouncedValue hook when none is provided.
 */
export const DEFAULT_DEBOUNCE_DELAY = 50;

/**
 * Describes the default batch size of how many elements to fetch in a single request when fetching bulk data from the files API.
 */
export const API_FETCH_DEFAULT_BATCH_SIZE = 10000;

/**
 * Describes the maximum value that the zoomData can have, before text does not get resized any more.
 * So if the user zooms past that threshold, the text augmentations wont get applied anymore.
 */
export const MAX_ZOOM_ADJUST = 16;
/**
 * Describes the lower bound that the zoom intervals can take on.
 */
export const ZOOM_INTERVAL_LOWER_BOUND = 4.4;
/**
 * Describes the upper bound that the zoom intervals can take on.
 */
export const ZOOM_INTERVAL_UPPER_BOUND = MAX_ZOOM_ADJUST;
/**
 * Describes the size that zoom intervals will be.
 * E.g. in what range the zoomData changes are not affecting the debounced value.
 */
export const ZOOM_INTERVAL_SIZE = 1.25;

/**
 * Describes the default distance that a rendered overlay indicator of a wall will have to the wall and in relation to its thickness.
 */
export const DEFAULT_WALL_OVERLAY_DISTANCE = 1.3;

/**
 * Describes the default time in milliseconds that the pulse animation related to the wall length change direction overlay uses.
 */
export const DEFAULT_OVERLAY_ANIMATION_DURATION = 1000;
/**
 * Describes the scaling factor that needs to get applied to the points of the old floorplanner plan.
 * USAGE: old point coordinate value * scale factor = new point coordinate value
 * Is only needed for the coordinate point transformations.
 * Lengths and distances of windows and doors are doubled and lengths of walls are expressed as centimeters in the old floorplan.
 */
export const GLTF_IMPORT_SCALE_FACTOR = 0.5;

export const GLTF_FONT_SIZE_SCALE_FACTOR = 0.17;

export const DEFAULT_EMPTY_TEXTURE: IAssetTexture = {
  id: -1,
  textureOrigin: ETextureOrigin.ASSET,
  type: null,
  name: '',
  material: null,
  color: undefined,
  rotation: 0,
  offset: 0,
};
